// client/src/v2/uso.ts — o RASTRO DE USO (marco 2 do Plano v1, 15/10/2026).
//
// O que a pessoa faz no app: cada tela (com o tempo VISÍVEL nela), as ações-chave
// do Círculo (usoAcao) e o fim de cada sessão. Vai em lote para POST /api/uso
// (server/uso/routes.js) a cada ~15 s e, quando o app some (visibilitychange
// hidden / pagehide), pelo sendBeacon — o beacon não leva cabeçalho, então o
// token vai no corpo, como text/plain.
//
// NUNCA atrasa a tela: registrar é empurrar num array; o envio é fetch keepalive
// sem await, e qualquer erro é engolido. Sem sessão o lote espera (até 200
// eventos) — quem entra depois de olhar a vitrine leva o que viu antes; ao sair
// da conta a fila é enviada com o token antigo e esvaziada.
//
// A prévia do editor (a página do Mentor num iframe) NÃO conta: é o próprio app
// dentro do app.
//
// SUÍTES: com localStorage.forja_teste = '1' (scripts/uso-cabecalho-teste.js),
// cada lote leva teste: true no corpo — o beacon do pagehide sai sem o
// cabeçalho X-Forja-Teste, então o corpo é o que garante is_test.
//
// Tela: o nome vem do endereço (/ → inicio, /mentor/x → mentor, /circulo/editor
// → circulo_editor, ids viram "id"); o path guarda só ?aba= e ?vista=. Passagens
// de menos de meio segundo (redirecionamentos) não contam. Quando o app volta do
// segundo plano, a mesma tela segue com meta.cont = true (soma no tempo, não é
// visita nova). Mais de 30 min fora = sessão nova.

import type { AuthSession } from '../types';

type Evento = {
  kind: 'tela' | 'acao' | 'sessao_fim';
  name: string;
  path?: string | null;
  meta?: Record<string, any> | null;
  ms?: number | null;
  t: number;
  session_id: string;
};

const INTERVALO = 15000;
const LOTE = 50;
const MAX_FILA = 200;
const TELA_MINIMA = 500;
const SESSAO_NOVA_DEPOIS = 30 * 60000;
const PARAMS_GUARDADOS = ['aba', 'vista'];

const ativo = typeof window !== 'undefined' && (() => { try { return window.parent === window; } catch { return false; } })();

const novoId = () => {
  try {
    const a = new Uint8Array(8);
    window.crypto.getRandomValues(a);
    return Array.from(a).map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch { return Math.random().toString(36).slice(2, 18); }
};

let sid = ativo ? novoId() : '';
let sessao: AuthSession | null = null;
let fila: Evento[] = [];
let tela: { name: string; path: string; meta: Record<string, any> | null; desde: number | null; cont: boolean } | null = null;
let sessaoDesde = Date.now();
let escondidoDesde: number | null = null;
let instalado = false;

const empurrar = (e: Omit<Evento, 't' | 'session_id'>) => {
  fila.push({ ...e, t: Date.now(), session_id: sid });
  if (fila.length > MAX_FILA) fila.splice(0, fila.length - MAX_FILA);
};

const ehId = (s: string) => /^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(s) || /^\d{3,}$/.test(s) || (s.length >= 16 && /\d/.test(s) && /^[A-Za-z0-9_-]+$/.test(s));
export function nomeDaTela(pathname: string): { name: string; meta: Record<string, any> | null } {
  const partes = (pathname || '/').split('/').filter(Boolean);
  if (!partes.length) return { name: 'inicio', meta: null };
  if (partes[0] === 'mentor' && partes[1]) {
    let slug = partes[1];
    try { slug = decodeURIComponent(slug); } catch { /* fica como veio */ }
    return { name: ['mentor', ...partes.slice(2)].join('_'), meta: { slug: slug.toLowerCase().slice(0, 60) } };
  }
  if (partes[0].startsWith('@')) return { name: 'perfil_publico', meta: null };
  return { name: partes.map((p) => (ehId(p) ? 'id' : p.toLowerCase())).join('_').replace(/[^a-z0-9_.-]/g, '_').slice(0, 80), meta: null };
}

function caminho(pathname: string, search: string): string {
  const sp = new URLSearchParams(search || '');
  const q = PARAMS_GUARDADOS.filter((k) => sp.get(k)).map((k) => `${k}=${encodeURIComponent(String(sp.get(k)).slice(0, 30))}`).join('&');
  return ((pathname || '/') + (q ? `?${q}` : '')).slice(0, 200);
}

// Fecha o trecho visível da tela atual (se houver) e devolve o evento.
function fecharTela(agora = Date.now()) {
  if (!tela || tela.desde === null) return;
  const ms = agora - tela.desde;
  tela.desde = null;
  if (ms < TELA_MINIMA) return;
  empurrar({ kind: 'tela', name: tela.name, path: tela.path, ms, meta: tela.cont ? { ...(tela.meta || {}), cont: true } : tela.meta });
}

const ehTeste = () => { try { return localStorage.getItem('forja_teste') === '1'; } catch { return false; } };

function enviar(eventos: Evento[], token: string, beacon: boolean) {
  const teste = ehTeste() ? { teste: true } : {};
  for (let i = 0; i < eventos.length; i += LOTE) {
    const parte = eventos.slice(i, i + LOTE);
    try {
      if (beacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
        const blob = new Blob([JSON.stringify({ t: token, eventos: parte, ...teste })], { type: 'text/plain' });
        if (navigator.sendBeacon('/api/uso', blob)) continue;
      }
      fetch('/api/uso', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ eventos: parte, ...teste }),
        keepalive: true,
      }).catch(() => {});
    } catch { /* o rastro nunca atrapalha */ }
  }
}

function despachar(beacon = false) {
  const token = sessao?.accessToken;
  if (!ativo || !token || !fila.length) return;
  const eventos = fila;
  fila = [];
  enviar(eventos, token, beacon);
}

function aoEsconder() {
  if (escondidoDesde !== null) { despachar(true); return; }
  const agora = Date.now();
  escondidoDesde = agora;
  fecharTela(agora);
  empurrar({ kind: 'sessao_fim', name: 'sessao_fim', path: tela?.path || null, ms: agora - sessaoDesde });
  despachar(true);
}

function aoVoltar() {
  if (escondidoDesde === null) return;
  const agora = Date.now();
  if (agora - escondidoDesde > SESSAO_NOVA_DEPOIS) sid = novoId();
  const mesmaSessao = agora - escondidoDesde <= SESSAO_NOVA_DEPOIS;
  escondidoDesde = null;
  sessaoDesde = agora;
  if (tela) { tela.desde = agora; tela.cont = mesmaSessao; }
}

function instalar() {
  if (instalado || !ativo) return;
  instalado = true;
  setInterval(() => despachar(false), INTERVALO);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') aoEsconder(); else aoVoltar();
  });
  window.addEventListener('pagehide', aoEsconder);
  if (document.visibilityState === 'hidden') escondidoDesde = Date.now();
}

/** A sessão atual (o token muda no refresh). Ao sair da conta, envia o que tem e esvazia. */
export function usoSessao(s: AuthSession | null) {
  if (!ativo) return;
  if (!s && sessao) { fecharTela(); despachar(false); fila = []; if (tela) tela.desde = escondidoDesde === null ? Date.now() : null; }
  sessao = s;
}

/** A tela mudou (troca de rota). */
export function usoTela(pathname: string, search = '') {
  if (!ativo) return;
  instalar();
  const path = caminho(pathname, search);
  if (tela && tela.path === path) return;
  fecharTela();
  const { name, meta } = nomeDaTela(pathname);
  tela = { name, path, meta, desde: escondidoDesde === null ? Date.now() : null, cont: false };
}

/** Uma ação-chave (editor_abrir, editor_salvar_perfil, mentor_abrir, assinar_clicar, episodio_tocar…). */
export function usoAcao(name: string, meta?: Record<string, any> | null) {
  if (!ativo) return;
  try { empurrar({ kind: 'acao', name: String(name).slice(0, 80), path: tela?.path || null, meta: meta || null }); } catch { /* nunca atrapalha */ }
}
